// App entry. Loaded as <script type="module" src="/scripts/main.ts">.
//
// All this code is plain progressive-enhancement — the site works without JS.
// Hooks are kept tiny so they don't pull in a framework.

// Bootstrap JS — only the components the site uses (navbar-toggler collapse
// and dropdowns). Importing a component registers its `data-bs-*` handlers.
// Add further components here when a page needs them (modal, offcanvas, ...).
import "bootstrap/js/dist/collapse";
import "bootstrap/js/dist/dropdown";

const onReady = (fn: () => void) => {
  if (document.readyState !== "loading") fn();
  else document.addEventListener("DOMContentLoaded", fn, { once: true });
};

onReady(() => {
  setFooterYear();
  initHeaderShadow();
  initSmoothAnchors();
  initTestimonials();
});

/** Set <span data-year> in the footer to the current year. */
function setFooterYear() {
  const year = new Date().getFullYear();
  for (const el of document.querySelectorAll<HTMLElement>("[data-year]")) {
    el.textContent = String(year);
  }
}

/**
 * Toggle Bootstrap's `shadow-sm` class on `<nav.navbar>` when the page
 * scrolls past a small threshold, so the header grows a subtle shadow
 * once content scrolls under it. Pure CSS handles the visual; this
 * script just flips the class.
 */
function initHeaderShadow() {
  const header = document.querySelector<HTMLElement>(".navbar");
  if (!header) return;
  const SCROLL_THRESHOLD = 4;
  let raf = 0;
  const update = () => {
    raf = 0;
    header.classList.toggle("shadow-sm", window.scrollY > SCROLL_THRESHOLD);
  };
  const onScroll = () => {
    if (raf) return;
    raf = requestAnimationFrame(update);
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  update();
}

/**
 * Smooth-scroll for in-page anchors (#section-id) accounting for the
 * sticky header. Falls back to native behavior if scrollIntoView fails.
 */
function initSmoothAnchors() {
  const links = document.querySelectorAll<HTMLAnchorElement>('a[href^="#"]');
  links.forEach((link) => {
    link.addEventListener("click", (e) => {
      const href = link.getAttribute("href");
      if (!href || href === "#") return;
      const target = document.querySelector<HTMLElement>(href);
      if (!target) return;
      e.preventDefault();
      const header = document.querySelector<HTMLElement>(".navbar");
      const offset = header ? header.offsetHeight : 0;
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
      // Update the URL hash without a jump.
      history.replaceState(null, "", href);
    });
  });
}

/**
 * Donate card: picking a preset amount clears the custom field, typing a
 * custom amount unchecks the presets, so only one amount is ever active.
 */
function initDonateForm() {
  const form = document.querySelector<HTMLFormElement>("[data-donate]");
  if (!form) return;
  const custom = form.querySelector<HTMLInputElement>("#amount-custom");
  const presets = form.querySelectorAll<HTMLInputElement>('input[name="amount"]');
  presets.forEach((radio) => {
    radio.addEventListener("change", () => {
      if (custom) custom.value = "";
    });
  });
  custom?.addEventListener("input", () => {
    presets.forEach((radio) => {
      radio.checked = false;
    });
  });
}

onReady(initDonateForm);

/**
 * Testimonial carousel: drag with the mouse to scroll (touch and trackpad
 * scroll natively), mark the card nearest the centre as active, and start
 * with the main card centred.
 */
function initTestimonials() {
  const track = document.querySelector<HTMLElement>(".testimonials");
  if (!track) return;
  const cards = Array.from(track.querySelectorAll<HTMLElement>(".testimonial"));
  const centre = (card: HTMLElement) => card.offsetLeft - track.offsetLeft + card.offsetWidth / 2 - track.clientWidth / 2;

  const markActive = () => {
    const middle = track.scrollLeft;
    let best = cards[0];
    for (const card of cards) {
      if (Math.abs(centre(card) - middle) < Math.abs(centre(best) - middle)) best = card;
    }
    for (const card of cards) card.classList.toggle("is-active", card === best);
  };

  const main = track.querySelector<HTMLElement>(".testimonial-main");
  if (main) track.scrollLeft = centre(main);
  track.classList.add("js-ready");
  markActive();
  track.addEventListener("scroll", () => requestAnimationFrame(markActive), { passive: true });

  let startX = 0;
  let startScroll = 0;
  let pressed = false;
  let moved = false;
  track.addEventListener("pointerdown", (e) => {
    if (e.pointerType !== "mouse" || e.button !== 0) return;
    pressed = true;
    moved = false;
    startX = e.clientX;
    startScroll = track.scrollLeft;
  });
  window.addEventListener("pointermove", (e) => {
    if (!pressed) return;
    const dx = e.clientX - startX;
    if (!moved && Math.abs(dx) > 4) {
      moved = true;
      track.classList.add("is-dragging");
    }
    if (moved) track.scrollLeft = startScroll - dx;
  });
  const release = () => {
    if (!pressed) return;
    pressed = false;
    track.classList.remove("is-dragging");
  };
  window.addEventListener("pointerup", release);
  window.addEventListener("pointercancel", release);
}
